import { motion } from "framer-motion";

/**
 * PaperCard — displays an ArXiv paper with title, authors, abstract excerpt,
 * and a relevance score badge. Used in the Chat page as citation cards.
 *
 * Props:
 *   paper: {
 *     arxiv_id, title, authors, abstract (or chunk_text),
 *     score, published, categories
 *   }
 *   index: number — stagger delay
 */
export default function PaperCard({ paper, index = 0 }) {
  const {
    arxiv_id,
    title,
    authors,
    abstract,
    chunk_text,
    score,
    published,
    categories = [],
  } = paper;

  const displayText = chunk_text || abstract || "";
  const truncated =
    displayText.length > 220 ? displayText.slice(0, 220) + "…" : displayText;
  const arxivUrl = `https://arxiv.org/pdf/${arxiv_id}`;
  const scoreLabel = score != null ? `${(score * 100).toFixed(0)}%` : null;

  const authorList = Array.isArray(authors)
    ? authors.slice(0, 3).join(", ") + (authors.length > 3 ? " et al." : "")
    : authors || "";

  return (
    <motion.article
      className="paper-card"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07, duration: 0.35 }}
    >
      <div className="paper-card__header">
        <div className="paper-card__meta">
          {scoreLabel && (
            <span className="paper-card__score badge">{scoreLabel} match</span>
          )}
          {categories.slice(0, 2).map((cat) => (
            <span key={cat} className="badge">
              {cat}
            </span>
          ))}
        </div>
        <a
          href={arxivUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="paper-card__link"
          aria-label="Open on ArXiv"
        >
          ↗
        </a>
      </div>

      <h3 className="paper-card__title">{title}</h3>

      {authorList && (
        <p className="paper-card__authors text-muted">{authorList}</p>
      )}

      {truncated && <p className="paper-card__excerpt">{truncated}</p>}

      <div className="paper-card__footer">
        <span className="paper-card__id text-muted">{arxiv_id}</span>
        {published && (
          <span className="paper-card__date text-muted">
            {new Date(published).getFullYear()}
          </span>
        )}
      </div>

      <style>{`
        .paper-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: var(--space-md) var(--space-lg);
          transition: border-color var(--transition-base), box-shadow var(--transition-base);
        }
        .paper-card:hover {
          border-color: var(--accent);
          box-shadow: var(--glow-gold);
        }
        .paper-card__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-sm);
          margin-bottom: var(--space-sm);
        }
        .paper-card__meta {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-xs);
        }
        .paper-card__score {
          background: var(--accent-soft);
          color: var(--accent);
        }
        .paper-card__link {
          color: var(--text-muted);
          font-size: 1rem;
          transition: color var(--transition-fast);
          flex-shrink: 0;
        }
        .paper-card__link:hover { color: var(--accent); opacity: 1; }
        .paper-card__title {
          font-size: 0.95rem;
          font-family: var(--font-body);
          font-weight: 600;
          color: var(--text);
          margin-bottom: var(--space-xs);
          line-height: 1.4;
        }
        .paper-card__authors {
          font-size: 0.8rem;
          margin-bottom: var(--space-sm);
        }
        .paper-card__excerpt {
          font-size: 0.82rem;
          color: var(--text-muted);
          line-height: 1.55;
          margin-bottom: var(--space-sm);
        }
        .paper-card__footer {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
        }
      `}</style>
    </motion.article>
  );
}
